<template>
  <div class="md-layout">
    <div class="md-layout-item">
      <md-card>
        <md-card-header
          v-if="title"
          class="md-card-header-icon md-card-header-green"
        >
          <div class="card-icon">
            <md-icon>assignment</md-icon>
          </div>
          <h4 class="title">{{ title }}</h4>
        </md-card-header>
        <md-card-content>
          <yv-form-search
            v-if="!!formSearch"
            :tableButton="tableButton"
            :formSearch="formSearch"
            :inlineSearchAction="inlineSearchAction"
            @search="search"
            @export="exportCSV"
            @update-params="setSearchingParams"
            @initialize-params="initializeParams"
            :enableExport="enableExport"
          >
            <template #search-actions="{ actions }">
              <slot name="search-actions" :actions="actions"></slot>
            </template>
          </yv-form-search>
          <p class="table-desc ml-[15px]">{{ tableDesc }}</p>
          <yv-table
            :tableData="tableData"
            :pagination="pagination"
            :columns="columns"
            @confirmDelete="confirmDelete"
            :hideAction="hideAction"
            :hideNoNumber="hideNoNumber"
            :currentRoute="currentRoute"
            :selectable="selectable"
            :bulkActions="bulkActions"
            :hideBulkAction="hideBulkAction"
            :selectedItems="selectedItems"
            :tableActionsComponent="tableActionsComponent"
            @selectItems="onSelectItems"
            @changePage="changePage"
            :sortData="getSortData"
            :sortable="sortable"
            @sort="sort"
            v-show="hasTableData"
            :permissionConfig="permissionConfig"
          />
          <no-data-found class="mx-auto" v-if="!hasTableData"></no-data-found>
        </md-card-content>
        <yv-pagination
          v-if="!shouldHidePagination && hasTableData"
          :pagination="pagination"
          @changePage="changePage"
        />
      </md-card>
    </div>
  </div>
</template>

<script>
import {
  YvPagination,
  YvTable,
  YvFormSearch
} from "@/components/Base/index.js";
import NoDataFound from "@/components/Base/Illustration/NoDataFound.vue";
import Swal from "sweetalert2";

export default {
  name: "YvIndex",
  data() {
    const defaultPage = 1;
    const defaultLimit = 10;

    return {
      pagination: {
        ...{
          perPage: this.limit || defaultLimit,
          from: 0,
          to: 0,
          total: 0,
          currentPage: defaultPage,
          hidePaging: false,
          hidePerPageOptions: false
        },
        ...this.paginationCfg
      },
      tableData: [],
      params: {
        page: 1,
        limit: this.limit || defaultLimit,
        ...this.additionalParams
      },
      modal: {
        show: false,
        action: "view"
      },
      modalAdd: {
        show: false
      },
      selectedItems: [],
      sortDataDefault: {
        currentSort: "id",
        currentSortOrder: "desc"
      }
    };
  },
  props: [
    "path",
    "formSearch",
    "columns",
    "formAdd",
    "enableExport",
    "title",
    "hideAction",
    "hideNoNumber",
    "disabledHeaderActions",
    "selectable",
    "bulkActions",
    "mainField",
    "formatTableData",
    "tableButton",
    "tableActionsComponent",
    "sortable",
    "sortData",
    "inlineSearchAction",
    "hideBulkAction",
    "paginationCfg",
    "dataSource",
    "tableDesc",
    "hasArrayRes",
    "permissionConfig",
    "limit",
    "additionalParams"
  ],
  components: { YvTable, YvPagination, YvFormSearch, NoDataFound },
  methods: {
    getTableData(shouldShowLoading = true) {
      if (this.hasStaticDataSource) {
        return this.getStaticTableData();
      }

      return this.fetchRemoteData(shouldShowLoading);
    },
    getStaticTableData() {
      this.clientSidePagination();
      this.tableData = this.dataSource.slice(
        this.pagination.from - 1, // ? bcz array idx from 0
        this.pagination.to
      );
    },
    fetchRemoteData(shouldShowLoading) {
      return this.$axios
        .get(
          this.path,
          this.params,
          undefined,
          undefined,
          undefined,
          undefined,
          shouldShowLoading
        )
        .then(response => {
          const resData = this.hasArrayRes ? response.data : response.data.data;
          const tableData = this.formatTableData
            ? this.formatTableData(resData)
            : resData;

          this.updateServerPaginationParams(response.data);
          this.tableData = this.addOrdinalColumn(tableData);

          return this.tableData;
        });
    },
    changePage(paginationParams) {
      this.setPaginationParams(paginationParams);
      this.getTableData();
    },
    search(customParams) {
      this.params.page = 1;
      this.pagination.currentPage = 1;
      if (customParams)
        this.params = {
          ...this.params,
          ...customParams,
          ...this.additionalParams
        };
      delete this.params.order_by;
      this.getTableData();
    },
    setSearchingParams(searchingParams) {
      Object.entries(searchingParams).forEach(([key, value]) => {
        this.$set(this.params, key, value);
      });
    },
    setPaginationParams(paginationParams) {
      this.params.page = this.pagination.currentPage;
      this.params.limit = this.pagination.perPage;
      this.params = { ...this.params, ...paginationParams };
    },
    initializeParams(searchingParams) {
      this.setSearchingParams(searchingParams);
      this.setPaginationParams();
    },
    updateServerPaginationParams(serverPagination) {
      this.pagination.total = serverPagination.total;
      this.pagination.from = serverPagination.from;
      this.pagination.to = serverPagination.to;
    },
    addOrdinalColumn(tableData) {
      if (!tableData) return tableData;

      const offsetPage = +this.pagination.currentPage - 1;
      const perPage = +this.pagination.perPage;

      return tableData.map((record, idx) => ({
        ...record,
        noNumb: offsetPage * perPage + (idx + 1) //? bcz array start from 0
      }));
    },
    sort(orderBy) {
      this.params = { ...this.params, ...orderBy };
      this.getTableData();
    },
    async delete(data) {
      const id = data.id || data._id;
      const deleteTitle = `You deleted ${data[this.mainField]} successfully !`;
      await this.$axios
        .delete(this.path + "/" + id)
        .then(() => {
          Swal.fire({
            title: "Deleted!",
            text: deleteTitle,
            type: "success",
            confirmButtonClass: "md-button md-success btn-fill",
            buttonsStyling: false
          });
        })
        .catch(error => {
          console.log(error);
        });

      // Reload table data
      this.getTableData();
    },
    confirmDelete(data) {
      Swal.fire({
        title: this.$t("common.are_you_sure"),
        text: this.$t("common.you_wont_be_able_to_revert_this"),
        type: "warning",
        showCancelButton: true,
        confirmButtonClass: "md-button md-success btn-fill",
        cancelButtonClass: "md-button md-danger btn-fill",
        confirmButtonText: this.$t("common.yes_delete_it"),
        buttonsStyling: false
      }).then(result => {
        if (result.value) {
          this.delete(data);
        }
      });
    },
    exportCSV() {
      this.$axios
        .get(this.path + "/export", this.params, true, {}, "blob")
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", this.path + ".csv");
          document.body.appendChild(link);
          link.click();
          link.remove();
        });
    },
    onSelectItems(items) {
      this.selectedItems = items;
    },
    clientSidePagination() {
      const offsetIdx =
        this.pagination.perPage * (this.pagination.currentPage - 1);
      const lastIdx = offsetIdx + this.pagination.perPage;

      this.pagination.from = offsetIdx + 1;
      this.pagination.to =
        lastIdx > this.dataSource.length ? this.dataSource.length : lastIdx;
    }
  },
  computed: {
    currentRoute() {
      return this.$route.path.endsWith("/")
        ? this.$route.path.slice(0, -1)
        : this.$route.path;
    },
    getSortData: function() {
      return this.sortData ? this.sortData : this.sortDataDefault;
    },

    shouldHidePagination() {
      return this.pagination?.hidePaging;
    },
    hasStaticDataSource() {
      return !this.path;
    },
    hasTableData() {
      return this.tableData?.length;
    }
  },
  watch: {
    dataSource() {
      if (this.hasStaticDataSource) {
        this.getStaticTableData();
      }
    },
    paginationCfg() {
      this.pagination = { ...this.pagination, ...this.paginationCfg };
    }
  }
};
</script>
<style scoped>
.md-card .md-card-actions {
  border: none;
  margin-top: 15px;
}
</style>
